import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FileTextIcon, UploadIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  BackLink,
  Button,
  CampaignTimeline,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNotice,
  FactList,
  Field,
  LoadingState,
  PageEyebrow,
  Panel,
  Pill,
  SearchInput,
  Tabs,
  TextArea,
} from '@bcecloud/portal-ui';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  addProposalDocument,
  CAMPAIGN_STAGE,
  CAMPAIGN_STAGE_LABELS,
  closeCampaign,
  readCampaign,
  readProposal,
  removeProposalDocument,
  sendProposal,
  type CampaignDetail,
  type CampaignEvent,
  type ProposalDocument,
} from '@/lib/campaigns';
import type { TimelineEvent } from '@bcecloud/portal-ui';
import { stageTone } from '@/pages/CampaignsPage';
import { ContractsTab } from '@/pages/event-contracts-tab';
import { CLIENT_LIST, ClientChecklists } from '@/pages/event-client-portal';

/**
 * One campaign inquiry from BCE's side: what the client sent, the proposal
 * BCE sends back, and (as the next units land) the agreement, the plan and
 * the timeline. The campaign is the client's record; BCE reads the same
 * view and adds its side.
 */

const TABS = [
  { key: 'inquiry', label: 'Inquiry' },
  { key: 'proposal', label: 'Proposal' },
  { key: 'agreement', label: 'Agreement' },
  { key: 'planning', label: 'Planning' },
  { key: 'timeline', label: 'Timeline' },
] as const;
type CampaignTab = (typeof TABS)[number]['key'];

/** The inquiry and proposal tabs search independently, each under its own URL key. */
const CAMPAIGN_SEARCH_KEYS = { events: 'eventsQ', documents: 'documentsQ' } as const;

/** A campaign event with the text its row shows, for the search. */
interface CampaignEventRow {
  readonly event: CampaignEvent;
  readonly team: string;
  readonly program: string;
  readonly reference: string;
  readonly place: string;
}

/** The fields each tab's search matches. */
const CAMPAIGN_EVENT_SEARCH_FIELDS: ReadonlyArray<keyof CampaignEventRow> = ['team', 'program', 'reference', 'place'];
const DOCUMENT_SEARCH_FIELDS: ReadonlyArray<keyof ProposalDocument> = ['name'];

function campaignEventRow(event: CampaignEvent): CampaignEventRow {
  return {
    event,
    team: event.teamName ?? '',
    program: event.workspace.programName ?? '',
    reference: event.workspace.reference ?? '',
    place: [event.workspace.venueName, [event.workspace.city, event.workspace.state].filter(Boolean).join(', ')].filter(Boolean).join(' · '),
  };
}

function asTab(value: string | null): CampaignTab {
  return TABS.some((tab) => tab.key === value) ? (value as CampaignTab) : 'inquiry';
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fileSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function CampaignDetailPage(): React.ReactElement {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const [params, setParams] = useSearchParams();
  const tab = asTab(params.get('tab'));
  const queryClient = useQueryClient();
  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  const campaign = useQuery({ queryKey: ['campaign', campaignId], queryFn: ({ signal }) => readCampaign(campaignId, signal) });
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
    await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  };
  const close = useMutation({
    mutationFn: () => closeCampaign(campaignId, closeReason.trim()),
    onSuccess: async () => {
      setClosing(false);
      await refresh();
    },
  });

  if (campaign.isPending) return <LoadingState label="Loading the campaign…" />;
  if (campaign.isError) return <ErrorNotice title="Campaign unavailable" message={errorMessage(campaign.error)} />;
  const data = campaign.data;
  const closed = data.stage === CAMPAIGN_STAGE.closed;

  return (
    <section className="animate-portal-rise">
      <BackLink to={`/organizations/${data.organization.id}?tab=campaigns`}>{data.organization.name}</BackLink>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageEyebrow>CAMPAIGN</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1.7px]">{data.campaign.name}</h1>
          <p className="mt-1 text-sm text-admin-muted">
            <Link to={`/organizations/${data.organization.id}?tab=campaigns`} className="text-primary underline-offset-2 hover:underline">
              {data.organization.name}
            </Link>
            {data.submission ? ` · sent ${longDate(data.submission.at)}${data.submission.byName ? ` by ${data.submission.byName}` : ''}` : ' · not sent yet'}
            {data.rollup.combinedGroupSize > 0 ? ` · ${data.rollup.combinedGroupSize.toLocaleString('en-US')} combined participants` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Pill tone={stageTone(data.stage)}>{CAMPAIGN_STAGE_LABELS[data.stage]}</Pill>
          <Button variant="secondary" href={data.portalUrl} target="_blank" rel="noreferrer">
            Open on their portal ↗
          </Button>
          {closed ? null : (
            <Button variant="secondary" onClick={() => setClosing(true)}>
              Close campaign
            </Button>
          )}
        </div>
      </header>

      {closed && data.campaign.closedReason ? (
        <p className="mt-4 text-portal-small text-portal-muted">Closed: {data.campaign.closedReason}</p>
      ) : null}

      <div className="mt-8">
        <Tabs
          label="Campaign sections"
          items={TABS.map((entry) => ({ key: entry.key, label: entry.label }))}
          active={tab}
          onChange={(key) =>
            setParams((previous) => {
              const next = new URLSearchParams(previous);
              next.set('tab', key);
              return next;
            })
          }
        />
        <div className="mt-6">
          {tab === 'inquiry' ? (
            <InquiryTab campaign={data} />
          ) : tab === 'proposal' ? (
            <ProposalTab campaign={data} onChanged={refresh} />
          ) : tab === 'agreement' ? (
            <ContractsTab subject={{ kind: 'campaign', id: campaignId }} />
          ) : tab === 'planning' ? (
            <ClientChecklists scope={{ kind: 'campaign', id: campaignId }} list={CLIENT_LIST.plan} />
          ) : (
            <TimelineTab campaign={data} />
          )}
        </div>
      </div>

      {closing ? (
        <Dialog open onOpenChange={(open) => !open && setClosing(false)}>
          <DialogContent className="max-w-md">
            <DialogTitle>Close this campaign?</DialogTitle>
            <p className="mt-2 text-portal-small text-portal-muted">
              The client sees it as closed on their portal. Nothing under it is deleted; their events and bookings stay where they are.
            </p>
            <div className="mt-4">
              <Field label="Why" htmlFor="campaign-close-reason" hint="A line for the record, for example “Budget pulled for 2026”.">
                <TextArea id="campaign-close-reason" rows={3} value={closeReason} onChange={(event) => setCloseReason(event.target.value)} />
              </Field>
            </div>
            {close.isError ? <ErrorNotice inline title="Not closed" message={errorMessage(close.error)} /> : null}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setClosing(false)} disabled={close.isPending}>
                Keep it open
              </Button>
              <Button onClick={() => close.mutate()} disabled={close.isPending || closeReason.trim().length === 0}>
                {close.isPending ? 'Closing…' : 'Close campaign'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </section>
  );
}

/** What the client sent: the shared facts and the events under it. */
function InquiryTab({ campaign }: { readonly campaign: CampaignDetail }): React.ReactElement {
  const facts = campaign.campaign;
  const { search, setSearch } = useUrlSearch(CAMPAIGN_SEARCH_KEYS.events);
  const eventRows = filterRows(campaign.events.map(campaignEventRow), search, CAMPAIGN_EVENT_SEARCH_FIELDS);
  const window =
    campaign.rollup.windowStart ?? facts.deliveryWindowStart
      ? `${shortDate(campaign.rollup.windowStart ?? facts.deliveryWindowStart)}${
          (campaign.rollup.windowEnd ?? facts.deliveryWindowEnd) && (campaign.rollup.windowEnd ?? facts.deliveryWindowEnd) !== (campaign.rollup.windowStart ?? facts.deliveryWindowStart)
            ? ` to ${shortDate(campaign.rollup.windowEnd ?? facts.deliveryWindowEnd)}`
            : ''
        }`
      : null;

  return (
    <div className="space-y-6">
      {campaign.submission ? (
        <Panel className="p-5">
          <CardHeader title="What they said" description={`Sent ${longDate(campaign.submission.at)}${campaign.submission.byName ? ` by ${campaign.submission.byName}` : ''}.`} />
          <p className="text-portal-small whitespace-pre-line">{campaign.submission.note ?? 'No note came with it.'}</p>
        </Panel>
      ) : (
        <Panel>
          <EmptyState title="Not sent yet" message="The client is still drafting this campaign. It appears here when they send it." />
        </Panel>
      )}

      {campaign.proposalResponses.length > 0 ? (
        <Panel className="p-5">
          <CardHeader title="Their reply to the proposal" description="The newest reply is the one to work from; earlier ones are kept below it." />
          <ol className="grid gap-4">
            {[...campaign.proposalResponses].reverse().map((response, index) => (
              <li key={`${response.at}-${index}`} className={index === 0 ? '' : 'border-t border-portal-line pt-4 text-portal-muted'}>
                <p className="text-portal-small">
                  <Pill tone={response.decision === 'GO_AHEAD' ? 'success' : 'warning'}>
                    {response.decision === 'GO_AHEAD' ? 'Accepted' : 'Changes requested'}
                  </Pill>{' '}
                  {longDate(response.at)}
                  {response.byName ? ` by ${response.byName}` : ''}
                </p>
                {response.note ? <p className="mt-2 text-portal-small whitespace-pre-line">{response.note}</p> : null}
              </li>
            ))}
          </ol>
        </Panel>
      ) : null}

      <Panel className="p-5">
        <CardHeader title="What every event shares" description="The client's shared facts, as they entered them on their portal." />
        <FactList
          columns={3}
          showEmpty
          facts={[
            { label: 'Objective', value: facts.objective || null },
            { label: 'Theme', value: facts.theme || null },
            { label: 'Approved branding and messaging', value: facts.approvedMessaging || null },
            { label: 'Approved program or menu', value: campaign.program?.name ?? facts.approvedProgram ?? null },
            { label: 'Executive sponsor', value: facts.executiveSponsor || null },
            { label: 'Delivery window', value: window },
            { label: 'Combined charity goals', value: facts.csrGoals || null },
            { label: 'Combined reporting requirements', value: facts.reportingRequirements || null },
            { label: 'Participating teams', value: (facts.participatingTeamIds ?? []).length > 0 ? `${(facts.participatingTeamIds ?? []).length} team${(facts.participatingTeamIds ?? []).length === 1 ? '' : 's'}` : null },
          ]}
        />
      </Panel>

      <Panel className="overflow-hidden">
        <div className="p-5 pb-4">
          <CardHeader className="mb-0" title="Events and inquiries" description="One per team. A booking request reads as an inquiry until it is approved, and an event after." />
          {campaign.events.length > 0 ? (
            <SearchInput
              label="Search events and inquiries"
              placeholder="Team, program, reference or place"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="mt-3 w-full max-w-sm"
            />
          ) : null}
        </div>
        {campaign.events.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState title="Nothing under it yet" message="The client links each team's booking or event to the campaign on their portal." />
          </div>
        ) : eventRows.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState title="No events or inquiries match" message="Try a different search." />
          </div>
        ) : (
          <DataTable flush headers={['Team', 'Event', 'Location', 'Date', 'Group', 'Stage']} rightAlignedHeaders={[4]}>
            {eventRows.map(({ event, place }) => (
              <tr key={event.id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small font-bold">{event.teamName ?? <span className="font-normal text-portal-muted">Not set</span>}</td>
                <td className="p-3 text-portal-small">
                  {event.workspace.eventId ? (
                    <Link to={`/events/${event.workspace.eventId}`} className="font-bold text-primary underline-offset-2 hover:underline">
                      {event.workspace.programName ?? event.workspace.reference ?? 'Open'}
                    </Link>
                  ) : (
                    <span className="font-bold">{event.workspace.programName ?? event.workspace.reference ?? 'Booking'}</span>
                  )}
                  {event.workspace.reference ? <span className="block text-portal-micro text-portal-muted">{event.workspace.reference}</span> : null}
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{place || '—'}</td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{shortDate(event.workspace.startAt)}</td>
                <td className="p-3 text-portal-small text-right">{event.workspace.finalParticipants ?? event.workspace.estimatedParticipants ?? '—'}</td>
                <td className="p-3 text-portal-small whitespace-nowrap">
                  <Pill tone={event.stage === 'EVENT' ? 'success' : 'warning'}>{event.stage === 'EVENT' ? 'Event' : 'Inquiry'}</Pill>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </Panel>
    </div>
  );
}

/** BCE's proposal: PDFs, added one by one, sent as one. */
function ProposalTab({ campaign, onChanged }: { readonly campaign: CampaignDetail; readonly onChanged: () => Promise<void> }): React.ReactElement {
  const campaignId = campaign.campaign._id;
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<readonly string[]>([]);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const { search, setSearch } = useUrlSearch(CAMPAIGN_SEARCH_KEYS.documents);

  const proposal = useQuery({ queryKey: ['campaign-proposal', campaignId], queryFn: ({ signal }) => readProposal(campaignId, signal) });
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['campaign-proposal', campaignId] });
    await onChanged();
  };

  async function upload(files: readonly File[]): Promise<void> {
    for (const file of files) {
      setUploading((previous) => [...previous, file.name]);
      try {
        await addProposalDocument(campaignId, file);
      } catch (error) {
        toast.error(`${file.name}: ${errorMessage(error)}`);
      } finally {
        setUploading((previous) => previous.filter((name) => name !== file.name));
      }
    }
    await refresh();
  }

  const remove = useMutation({
    mutationFn: (attachmentId: string) => removeProposalDocument(campaignId, attachmentId),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });
  const send = useMutation({
    mutationFn: () => sendProposal(campaignId, note),
    onSuccess: async () => {
      setSending(false);
      setNote('');
      toast.success('Proposal sent. The client’s admins have been told on their portal.');
      await refresh();
    },
  });

  if (proposal.isPending) return <LoadingState label="Loading the proposal…" />;
  if (proposal.isError) return <ErrorNotice title="Proposal unavailable" message={errorMessage(proposal.error)} />;
  const sent = proposal.data.sentAt !== null;
  const canSend = campaign.submission !== null && !sent && campaign.stage !== CAMPAIGN_STAGE.closed;
  const documents = proposal.data.documents;
  const documentRows = filterRows(documents, search, DOCUMENT_SEARCH_FIELDS);

  return (
    <div className="space-y-6">
      <input
        ref={fileInput}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) void upload(files);
          event.target.value = '';
        }}
      />

      {sent ? (
        <Panel className="p-5">
          <CardHeader
            title="Sent"
            description={`${longDate(proposal.data.sentAt as string)}${proposal.data.sentByName ? ` by ${proposal.data.sentByName}` : ''}. The client’s admins were told on their portal.`}
          />
          {proposal.data.note ? <p className="text-portal-small whitespace-pre-line">{proposal.data.note}</p> : null}
        </Panel>
      ) : campaign.submission === null ? (
        <Panel>
          <EmptyState title="Waiting on the client" message="The proposal goes out once the client has sent the campaign. You can add PDFs now and send when they do." />
        </Panel>
      ) : null}

      <Panel className="overflow-hidden">
        <div className="p-5 pb-4">
          <CardHeader
            className="mb-0"
            title="Documents"
            description={sent ? 'What the client sees now. Documents can still be added or taken away.' : 'Add one or more PDFs, then send them as one proposal. The client sees nothing until you send.'}
            aside={
              <span className="flex items-center gap-2">
                <Button variant="secondary" size="compact" onClick={() => fileInput.current?.click()} disabled={uploading.length > 0}>
                  <UploadIcon aria-hidden className="size-4" />
                  {uploading.length > 0 ? 'Uploading…' : 'Add PDF'}
                </Button>
                {sent ? null : (
                  <Button size="compact" onClick={() => setSending(true)} disabled={!canSend || documents.length === 0 || uploading.length > 0}>
                    Send proposal
                  </Button>
                )}
              </span>
            }
          />
          {documents.length > 0 ? (
            <SearchInput
              label="Search documents"
              placeholder="File name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="mt-3 w-full max-w-sm"
            />
          ) : null}
        </div>
        {documents.length === 0 && uploading.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState
              icon={<FileTextIcon aria-hidden className="size-6" />}
              title="No documents yet"
              message="Add the proposal as one or more PDFs: the proposal itself, pricing, the program outline, whatever the client should read."
              action={
                <Button onClick={() => fileInput.current?.click()}>
                  <UploadIcon aria-hidden className="size-4" />
                  Add PDF
                </Button>
              }
            />
          </div>
        ) : documentRows.length === 0 && uploading.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState title="No documents match" message="Try a different search." />
          </div>
        ) : (
          <DataTable flush headers={['Document', 'Size', 'Added', 'Actions']} visuallyHiddenHeaders={[3]}>
            {documentRows.map((document: ProposalDocument) => (
              <tr key={document.id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <Link to={`/attachments/${document.id}`} className="font-bold text-primary underline-offset-2 hover:underline">
                    {document.name}
                  </Link>
                </td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{fileSize(document.size)}</td>
                <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{shortDate(document.uploadedAt)}</td>
                <td className="p-3 text-right">
                  <Button variant="secondary" size="compact" onClick={() => remove.mutate(document.id)} disabled={remove.isPending}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {uploading.map((name) => (
              <tr key={`uploading-${name}`} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small text-portal-muted" colSpan={4}>
                  Uploading {name}…
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </Panel>

      {sending ? (
        <Dialog open onOpenChange={(open) => !open && setSending(false)}>
          <DialogContent className="max-w-md">
            <DialogTitle>Send the proposal?</DialogTitle>
            <p className="mt-2 text-portal-small text-portal-muted">
              {documents.length} document{documents.length === 1 ? '' : 's'} go to {campaign.organization.name}. Their admins are told on their portal, and by email only if they turned that on. You can still add or remove documents afterwards; the client sees the current set.
            </p>
            <div className="mt-4">
              <Field label="A note to go with it" htmlFor="proposal-note" hint="Optional. Shown with the documents on their portal, for example what to read first or when you will follow up.">
                <TextArea id="proposal-note" rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
              </Field>
            </div>
            {send.isError ? <ErrorNotice inline title="Not sent" message={errorMessage(send.error)} /> : null}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setSending(false)} disabled={send.isPending}>
                Not yet
              </Button>
              <Button onClick={() => send.mutate()} disabled={send.isPending}>
                {send.isPending ? 'Sending…' : 'Send proposal'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

/** When every event happens, on one axis. */
function TimelineTab({ campaign }: { readonly campaign: CampaignDetail }): React.ReactElement {
  return (
    <Panel className="p-5">
      <CardHeader
        title="Timeline"
        description="Every event under the campaign in date order, with today marked. Events with no date yet are listed underneath."
      />
      <CampaignTimeline
        events={campaign.events.map((event) => timelineEventOf(event))}
        emptyMessage="Events appear here once the client links them to the campaign."
      />
    </Panel>
  );
}

function timelineEventOf(event: CampaignEvent): TimelineEvent {
  const place = [event.workspace.venueName, [event.workspace.city, event.workspace.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
  const isEvent = event.stage === 'EVENT';
  return {
    id: event.id,
    title: event.workspace.programName ?? event.workspace.reference ?? 'Event',
    subtitle: [event.teamName, place].filter(Boolean).join(' · ') || null,
    startAt: event.workspace.startAt ?? null,
    stage: isEvent ? { label: 'Event', tone: 'success' } : { label: 'Inquiry', tone: 'warning' },
    href: event.workspace.eventId ? `/events/${event.workspace.eventId}` : undefined,
  };
}
